import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../contexts/NotificationContext";
import { useUnreadMessages } from "../../hooks/useUnreadMessages";
import { useConnectNotifications } from "../../hooks/useConnectNotifications";
import { io } from "socket.io-client";

import { Search, UserPlus, MessageCircle, Check, X, Clock, Users, LinkIcon, Share2, Trash2, Send, Stethoscope, Hammer, Calendar } from "lucide-react";
import { getApiUrl } from "../../config/api";

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function Connect() {
  const [activeTab, setActiveTab] = useState("messages");
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [connectionRequests, setConnectionRequests] = useState([]);
  const [sentConnectionRequests, setSentConnectionRequests] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [showVetModal, setShowVetModal] = useState(false);
  const [veterinarians, setVeterinarians] = useState([]);
  const [selectedVet, setSelectedVet] = useState(null);
  const [isLoadingVets, setIsLoadingVets] = useState(false);
  const [showFarrierModal, setShowFarrierModal] = useState(false);
  const [farriers, setFarriers] = useState([]);
  const [selectedFarrier, setSelectedFarrier] = useState(null);
  const [isLoadingFarriers, setIsLoadingFarriers] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  // Track last message counts per conversation to avoid false toasts when switching
  const [lastMessageCountByConversation, setLastMessageCountByConversation] = useState({});
  const lastLoadedConversationIdRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { refreshCount, refreshCountAndNotify } = useUnreadMessages();
  const { refreshNotifications } = useConnectNotifications();
  
  // Group conversation states
  const [showCreateCampfireModal, setShowCreateCampfireModal] = useState(false);
  const [newCampfireName, setNewCampfireName] = useState("");
  const [newCampfireDescription, setNewCampfireDescription] = useState("");
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [usersToInvite, setUsersToInvite] = useState([]); // For create mode

  const [isCreatingCampfire, setIsCreatingCampfire] = useState(false);
  const [groupParticipants, setGroupParticipants] = useState([]);
  
  // Participant search states
  const [showParticipantSearchModal, setShowParticipantSearchModal] = useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = useState("");
  const [participantSearchResults, setParticipantSearchResults] = useState([]);
  const [isSearchingParticipants, setIsSearchingParticipants] = useState(false);
  const [searchMode, setSearchMode] = useState('create'); // 'create' or 'edit'
  
  // Invite states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSearchTerm, setInviteSearchTerm] = useState("");
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [isSearchingInvites, setIsSearchingInvites] = useState(false);
  const [selectedInviteUser, setSelectedInviteUser] = useState(null);
  const [inviteMessage, setInviteMessage] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [currentInviteGroup, setCurrentInviteGroup] = useState(null);
  const [pendingInvitesByGroup, setPendingInvitesByGroup] = useState({});

  // Campfire management states
  const [showEditCampfireModal, setShowEditCampfireModal] = useState(false);
  const [editingCampfire, setEditingCampfire] = useState(null);
  const [editCampfireName, setEditCampfireName] = useState("");
  const [editCampfireDescription, setEditCampfireDescription] = useState("");
  const [editCampfireIsPublic, setEditCampfireIsPublic] = useState(false);

  const [isUpdatingCampfire, setIsUpdatingCampfire] = useState(false);
  const [showManageParticipantsModal, setShowManageParticipantsModal] = useState(false);
  const [managingCampfire, setManagingCampfire] = useState(null);
  const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);
  const [showDeleteCampfireConfirm, setShowDeleteCampfireConfirm] = useState(false);
  const [campfireToDelete, setCampfireToDelete] = useState(null);
  const [showRemoveParticipantConfirm, setShowRemoveParticipantConfirm] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState(null);

  // Contact sharing states
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [contactToAdd, setContactToAdd] = useState(null);
  const [contactType, setContactType] = useState('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [isSharingContact, setIsSharingContact] = useState(false);

  // Function to format last message content for conversation list
  const formatLastMessageContent = useCallback((message, isGroup, currentUserId) => {
    if (!message) return "No messages yet";
    
    // Determine if we should show sender name (for group conversations)
    const senderPrefix = isGroup && message.sender_name ? `${message.sender_name}: ` : "";
    
    if (message.type === "vet_share") {
      return `${senderPrefix}🏥 Shared a veterinarian`;
    } else if (message.type === "farrier_share") {
      return `${senderPrefix}🔨 Shared a farrier`;
    } else if (message.type === "event_share") {
      return `${senderPrefix}📅 Shared an event`;
    } else if (message.type === "stream_notification") {
      return `${senderPrefix}🔥 Started a live stream`;
    } else if (message.type === "system") {
      return "System message";
    } else if (message.type === "text") {
      return `${senderPrefix}${message.content}`;
    }
    
    return "Message";
  }, []);

  // Function to play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      // Audio notification not supported
    }
  }, []);

  // Function to scroll to bottom of messages
  const scrollToBottom = useCallback((instant = false) => {
    if (instant) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Function to manually reconnect WebSocket
  const reconnectWebSocket = useCallback(() => {
    if (socket) {
      socket.connect();
    }
  }, [socket]);

  // Initialize WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Create socket connection
    const newSocket = io(getApiUrl(''), {
      auth: {
        token: token
      },
      path: '/socket.io/', // Explicitly specify Socket.IO path
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      forceNew: true // Force new connection
    });

    // Socket event handlers
    newSocket.on('connect', () => {
      setSocketConnected(true);
      
      // Get user ID from token and join user room
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.userId) {
          newSocket.emit('join-user', payload.userId);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error parsing token:', error);
        }
        // Don't emit join-user if token is invalid
      }
    });

    newSocket.on('disconnect', () => {
      setSocketConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      setSocketConnected(false);
      if (process.env.NODE_ENV !== 'production') {
        console.error('Socket connection error:', error);
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      setSocketConnected(true);
      
      // Re-join user room after reconnection
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.userId) {
          newSocket.emit('join-user', payload.userId);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error parsing token:', error);
        }
        // Don't emit join-user if token is invalid
      }
    });

    newSocket.on('reconnect_attempt', (attemptNumber) => {
      // WebSocket reconnection attempt
    });

    newSocket.on('reconnect_failed', () => {
      setSocketConnected(false);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.close();
    };
  }, []);



  // Auto-scroll to bottom when messages change (only for new messages, not when scrolling up)
  useEffect(() => {
    // Only auto-scroll if we're at or near the bottom of the messages
    // This prevents auto-scrolling when user is reading older messages
    if (messages.length > 0 && messagesEndRef.current) {
      const messagesContainer = messagesEndRef.current.parentElement;
      if (messagesContainer) {
        const isNearBottom = messagesContainer.scrollTop + messagesContainer.clientHeight >= messagesContainer.scrollHeight - 100;
        if (isNearBottom) {
          scrollToBottom(false); // Use smooth scrolling for new messages
        }
      }
    }
  }, [messages, scrollToBottom]);

  // Simple initialization effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);





  // WebSocket message handling
  useEffect(() => {
    if (!socket || !selectedConversation) return;

    // Join conversation room
    socket.emit('join-conversation', selectedConversation.id);

    // Listen for new messages in this conversation
    const handleNewMessage = (message) => {
      // Check if this message is from the current user by comparing user IDs
      let isOwnMessage = false;
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const currentUserId = payload.userId;
          
          // Convert both IDs to strings for comparison to handle different data types
          isOwnMessage = String(message.user_id) === String(currentUserId);
        }
      } catch (error) {
        console.error('Error parsing token for user ID:', error);
        // If we can't determine user ID, assume it's not our message to be safe
        isOwnMessage = false;
      }
      
      // Only add the message to state if it's not already there (prevents duplicates)
      setMessages(prev => {
        // Check if message already exists to prevent duplicates
        const messageExists = prev.some(msg => msg.id === message.id);
        if (messageExists) {
          return prev;
        }
        return [...prev, message];
      });
      
      // Show notification for new messages from other users only
      if (!isOwnMessage) {
        showSuccess(`New message from ${message.user_name}!`);
        playNotificationSound();
      }
      
      // Refresh unread count
      refreshCountAndNotify();
    };

    // Listen for unread count updates
    const handleUnreadCountUpdate = (data) => {
      // Dispatch custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('unread-count-updated', { 
        detail: { count: data.count } 
      }));
    };

    socket.on('new-message', handleNewMessage);
    socket.on('unread-count-updated', handleUnreadCountUpdate);

    // Cleanup: leave conversation room and remove listener
    return () => {
      socket.emit('leave-conversation', selectedConversation.id);
      socket.off('new-message', handleNewMessage);
      socket.off('unread-count-updated', handleUnreadCountUpdate);
    };
  }, [socket, selectedConversation, refreshCountAndNotify]);



  // Remove debounced search effect since we now require exact matches
  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (activeTab === "discover") {
  //       fetchUsers(1, searchTerm);
  //     }
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [searchTerm, activeTab]);



  const fetchVeterinarians = useCallback(async () => {
    setIsLoadingVets(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getApiUrl("/api/veterinarians")}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVeterinarians(response.data);
    } catch (err) {
      console.error("Error fetching veterinarians:", err);
      showError("Failed to fetch veterinarians");
    } finally {
      setIsLoadingVets(false);
    }
  }, []);

  // Participant search functionality (like invite system)
  const searchParticipants = useCallback(async (searchTerm) => {
    if (!searchTerm.trim()) {
      setParticipantSearchResults([]);
      return;
    }

    setIsSearchingParticipants(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${getApiUrl("/api/connect/users")}?search=${encodeURIComponent(searchTerm.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter out users who are already participants (like invite system)
      const filteredUsers = response.data.users.filter(user => 
        !selectedParticipants.some(participant => participant.id === user.id)
      );
      
      setParticipantSearchResults(filteredUsers);
    } catch (err) {
      console.error("Error searching participants:", err);
      showError("Failed to search participants");
    } finally {
      setIsSearchingParticipants(false);
    }
  }, [selectedParticipants]);

  const sendParticipantInvite = useCallback(async (user, campfire) => {
    if (!user) {
      showError("Invalid user");
      return;
    }

    // For create mode, add to usersToInvite list
    if (searchMode === 'create') {
      if (!usersToInvite.some(u => u.id === user.id)) {
        setUsersToInvite(prev => [...prev, user]);
        showSuccess(`${user.name} added to invite list! 🔥`);
      } else {
        showError("User already in invite list");
      }
      setParticipantSearchTerm("");
      setParticipantSearchResults([]);
      return;
    }

    // For edit mode, send invitation immediately
    if (!campfire || !campfire.id) {
      showError("Invalid campfire");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      
      const response = await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${campfire.id}/invite`,
        {
          userId: user.id,
          message: `You're invited to join ${campfire.name}!`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(`Invitation sent to ${user.name}! 🔥`);
      
      // Add the user to pending invitations for this group
      setPendingInvitesByGroup((prev) => {
        const groupId = campfire.id;
        const existing = prev[groupId] || [];
        if (existing.some(u => u.id === user.id)) return prev;
        const nextForGroup = [
          ...existing,
          {
            id: user.id,
            name: user.name,
            email: user.email,
            role: 'member',
            pendingInvite: true
          }
        ];
        return { ...prev, [groupId]: nextForGroup };
      });
      
      setParticipantSearchTerm("");
      setParticipantSearchResults([]);
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      
      let errorMessage = err.response?.data?.error || 
                        err.response?.data?.message || 
                        err.message || 
                        "Failed to send invitation";
      
      // Check for duplicate invitation error
      const errorDetail = err.response?.data?.detail || '';
      if (errorMessage.includes('duplicate key') || 
          errorDetail.includes('already exists') ||
          errorMessage.includes('unique constraint')) {
        errorMessage = "This user has already been invited to this campfire.";
      } 
      
      showError(`Failed to send invitation: ${errorMessage}`); 
    }
  }, [searchMode, usersToInvite, showSuccess, showError, refreshNotifications]);

  const removeSelectedParticipant = useCallback((userId) => {
    setSelectedParticipants(prev => prev.filter(participant => participant.id !== userId));
  }, []);

  const removeUserFromInviteList = useCallback((userId) => {
    setUsersToInvite(prev => prev.filter(user => user.id !== userId));
  }, []);

  const openParticipantSearch = useCallback((mode, campfire = null) => {
    setSearchMode(mode);
    setParticipantSearchTerm("");
    setParticipantSearchResults([]);
    setShowParticipantSearchModal(true);
    // Store the campfire for sending invites
    if (campfire) {
      setEditingCampfire(campfire);
    }
  }, []);

  const clearSelectedParticipants = useCallback(() => {
    setSelectedParticipants([]);
    setUsersToInvite([]);
  }, []);

  const openVetModal = useCallback(() => {
    if (!selectedConversation) {
      showError("Please select a conversation first");
      return;
    }
    if (veterinarians.length === 0) {
      fetchVeterinarians();
    }
    setShowVetModal(true);
  }, [veterinarians.length, fetchVeterinarians, selectedConversation, showError]);

  const fetchFarriers = useCallback(async () => {
    setIsLoadingFarriers(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getApiUrl("/api/farriers")}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFarriers(response.data);
    } catch (err) {
      console.error("Error fetching farriers:", err);
      showError("Failed to fetch farriers");
    } finally {
      setIsLoadingFarriers(false);
    }
  }, []);

   const openFarrierModal = useCallback(() => {
    if (!selectedConversation) {
      showError("Please select a conversation first");
      return;
    }
    if (farriers.length === 0) {
      fetchFarriers();
    }
    setShowFarrierModal(true);
  }, [farriers.length, fetchFarriers, selectedConversation, showError]);

  const fetchEvents = useCallback(async () => {
    setIsLoadingEvents(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/calendar/events"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      showError("Failed to load events");
    } finally {
      setIsLoadingEvents(false);
    }
  }, [showError]);

  const openEventModal = useCallback(() => {
    if (!selectedConversation) {
      showError("Please select a conversation first");
      return;
    }
    if (events.length === 0) {
      fetchEvents();
    }
    setShowEventModal(true);
  }, [events.length, fetchEvents, selectedConversation, showError]);



  const fetchUsers = useCallback(async (page = 1, search = "") => {
    setIsLoadingUsers(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getApiUrl("/api/connect/users")}?page=${page}&limit=12&search=${search}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching users:", err);
      showError("Failed to load users");
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (searchTerm.trim()) {
      setHasSearched(true);
      fetchUsers(1, searchTerm.trim());
    }
  }, [searchTerm, fetchUsers]);

  const clearSearch = useCallback(() => {
    setSearchTerm("");
    setHasSearched(false);
    setUsers([]);
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      hasNextPage: false,
      hasPrevPage: false
    });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/connect/conversations"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setConversations(response.data);
      
      // Fetch pending invitations for all owned campfires
      const ownedCampfires = response.data.filter(c => c.isGroup && c.userRole === 'owner' && !c.isPublic);
      for (const campfire of ownedCampfires) {
        try {
          const pendingResponse = await axios.get(
            `${getApiUrl("/api/connect/conversations")}/${campfire.id}/pending-invitations`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setPendingInvitesByGroup(prev => ({
            ...prev,
            [campfire.id]: pendingResponse.data.map(invite => ({
              id: invite.to_user_id,
              name: invite.user_name,
              email: invite.user_email,
              role: 'member',
              pendingInvite: true
            }))
          }));
        } catch (err) {
          // Don't show error for individual campfire pending invitations
        }
      }
      
      return response.data;
    } catch (err) {
      console.error("Error fetching conversations:", err);
      throw err;
    }
  }, []);

  const fetchPendingInvitations = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/connect/invitations"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      
      // Ensure we always set an array, even if the API returns something else
      const invitations = Array.isArray(response.data) ? response.data : [];
      setPendingInvitations(invitations);
      return invitations;
    } catch (err) {
      // Set empty array on error to prevent map errors
      setPendingInvitations([]);
      throw err;
    }
  }, []);

  // Group conversation functions
  const joinGroup = useCallback(async (groupId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${groupId}/join`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Successfully joined the campfire! 🔥");
      await fetchConversations();
      await fetchPendingInvitations();
      setActiveTab("messages");
    } catch (err) {
      console.error("Error joining group:", err);
      showError(err.response?.data?.error || "Failed to join group");
    }
  }, [fetchConversations, fetchPendingInvitations]);

  const acceptInvitation = useCallback(async (invitationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/invitations")}/${invitationId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Invitation accepted! 🔥");
      await fetchPendingInvitations();
      await fetchConversations();
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      showError(err.response?.data?.error || "Failed to accept invitation");
    }
  }, [fetchPendingInvitations, fetchConversations, refreshNotifications]);

  const declineInvitation = useCallback(async (invitationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/invitations")}/${invitationId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Invitation declined");
      await fetchPendingInvitations();
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      console.error("Error declining invitation:", err);
      showError(err.response?.data?.error || "Failed to decline invitation");
    }
  }, [fetchPendingInvitations, refreshNotifications]);

  const leaveGroup = useCallback(async (groupId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${groupId}/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Successfully left the campfire");
      await fetchConversations();
      if (selectedConversation?.id === groupId) {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error("Error leaving group:", err);
      showError(err.response?.data?.error || "Failed to leave group");
    }
  }, [fetchConversations, selectedConversation]);

  const confirmDeleteCampfire = useCallback((campfire) => {
    setCampfireToDelete(campfire);
    setShowDeleteCampfireConfirm(true);
  }, []);

  const deleteCampfire = useCallback(async () => {
    if (!campfireToDelete) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${getApiUrl("/api/connect/conversations")}/${campfireToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Campfire deleted successfully");
      await fetchConversations();
      if (selectedConversation?.id === campfireToDelete.id) {
        setSelectedConversation(null);
      }
      setShowDeleteCampfireConfirm(false);
      setCampfireToDelete(null);
    } catch (err) {
      console.error("Error deleting campfire:", err);
      showError(err.response?.data?.error || "Failed to delete campfire");
    }
  }, [campfireToDelete, fetchConversations, selectedConversation]);

  const fetchGroupParticipants = useCallback(async (groupId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${getApiUrl("/api/connect/conversations")}/${groupId}/participants`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGroupParticipants(response.data);
    } catch (err) {
      console.error("Error fetching group participants:", err);
      showError("Failed to load group participants");
    }
  }, []);

  const fetchGroupPendingInvitations = useCallback(async (groupId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${getApiUrl("/api/connect/conversations")}/${groupId}/pending-invitations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Convert the response to the format expected by pendingInvitesByGroup
      const pendingInvites = response.data.map(invite => ({
        id: invite.to_user_id,
        name: invite.user_name,
        email: invite.user_email,
        role: 'member',
        pendingInvite: true
      }));
      
      setPendingInvitesByGroup(prev => ({
        ...prev,
        [groupId]: pendingInvites
      }));
    } catch (err) {
      console.error("Error fetching group pending invitations:", err);
      // Don't show error to user as this is not critical
    }
  }, []);

  // Invite functions
  const openInviteModal = useCallback((group) => {
    setCurrentInviteGroup(group);
    setInviteSearchTerm("");
    setInviteSearchResults([]);
    setSelectedInviteUser(null);
    setInviteMessage("");
    setShowInviteModal(true);
  }, []);

  const searchUsersForInvite = useCallback(async () => {
    if (!inviteSearchTerm.trim()) {
      setInviteSearchResults([]);
      return;
    }

    setIsSearchingInvites(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${getApiUrl("/api/connect/users")}?search=${encodeURIComponent(inviteSearchTerm.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filter out users who are already participants
      const filteredUsers = response.data.users.filter(user => 
        !groupParticipants.some(participant => participant.id === user.id)
      );
      
      setInviteSearchResults(filteredUsers);
    } catch (err) {
      console.error("Error searching users for invite:", err);
      showError("Failed to search users");
    } finally {
      setIsSearchingInvites(false);
    }
  }, [inviteSearchTerm, groupParticipants]);

  const sendInvite = useCallback(async () => {
    if (!selectedInviteUser || !currentInviteGroup) {
      showError("Please select a user to invite");
      return;
    }

    setIsSendingInvite(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${currentInviteGroup.id}/invite`,
        {
          userId: selectedInviteUser.id,
          message: inviteMessage || `You're invited to join ${currentInviteGroup.name}!`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(`Invitation sent to ${currentInviteGroup.name}! 🔥`);
      // Optimistically add a pending member to the list for this group
      setPendingInvitesByGroup((prev) => {
        const groupId = currentInviteGroup.id;
        const existing = prev[groupId] || [];
        if (existing.some(u => u.id === selectedInviteUser.id)) return prev;
        const nextForGroup = [
          ...existing,
          {
            id: selectedInviteUser.id,
            name: selectedInviteUser.name,
            email: selectedInviteUser.email,
            role: 'member',
            pendingInvite: true
          }
        ];
        return { ...prev, [groupId]: nextForGroup };
      });
      setShowInviteModal(false);
      setSelectedInviteUser(null);
      setInviteMessage("");
      setCurrentInviteGroup(null);
      // Do not force-refresh; pending list will render immediately.
    } catch (err) {
      console.error("Error sending invite:", err);
      showError(err.response?.data?.error || "Failed to send invitation");
    } finally {
      setIsSendingInvite(false);
    }
  }, [selectedInviteUser, currentInviteGroup, inviteMessage, fetchGroupParticipants]);

  // Campfire management functions
  const openEditCampfireModal = useCallback(async (campfire) => {
    // Only owners and admins can edit campfires
    if (campfire.userRole !== 'owner' && campfire.userRole !== 'admin') {
      showError("You don't have permission to edit this campfire");
      return;
    }
    
    setEditingCampfire(campfire);
    setEditCampfireName(campfire.name);
    setEditCampfireDescription(campfire.description);
    setEditCampfireIsPublic(campfire.isPublic !== false);
    
    // Fetch current participants and pending invitations to show in the edit modal
    try {
      await Promise.all([
        fetchGroupParticipants(campfire.id),
        fetchGroupPendingInvitations(campfire.id)
      ]);
    } catch (err) {
      console.error("Error fetching participants for edit modal:", err);
    }
    
    setShowEditCampfireModal(true);
  }, [fetchGroupParticipants, fetchGroupPendingInvitations]);

  const confirmRemoveParticipant = useCallback((participantId, participantName) => {
    // Additional permission checks
    if (editingCampfire?.userRole === 'admin') {
      // Admins can't remove owners
      const participant = groupParticipants.find(p => p.id === participantId);
      if (participant && participant.role === 'owner') {
        showError("Admins cannot remove campfire owners");
        return;
      }
    }
    
    setParticipantToRemove({ id: participantId, name: participantName });
    setShowRemoveParticipantConfirm(true);
  }, [editingCampfire, groupParticipants]);

  const removeParticipantFromEdit = useCallback(async () => {
    if (!editingCampfire || !participantToRemove) return;
    
    setIsRemovingParticipant(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${getApiUrl("/api/connect/conversations")}/${editingCampfire.id}/participants/${participantToRemove.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(`${participantToRemove.name} has been removed from the campfire`);
      
      // Refresh participants list
      await fetchGroupParticipants(editingCampfire.id);
      
      // Refresh conversations list to update member counts
      await fetchConversations();
      
      // Refresh conversations if this affects the current user
      if (participantToRemove.id === localStorage.getItem('userId')) {
        setSelectedConversation(null);
      }
      
      // Close confirmation modal
      setShowRemoveParticipantConfirm(false);
      setParticipantToRemove(null);
    } catch (err) {
      console.error("Error removing participant:", err);
      showError(err.response?.data?.error || "Failed to remove participant");
    } finally {
      setIsRemovingParticipant(false);
    }
  }, [editingCampfire, participantToRemove, fetchGroupParticipants, fetchConversations]);

  const removePendingInvitation = useCallback((userId, userName) => {
    if (!editingCampfire) return;
    
    // Remove from pending invitations state
    setPendingInvitesByGroup(prev => {
      const groupId = editingCampfire.id;
      const existing = prev[groupId] || [];
      const updated = existing.filter(u => u.id !== userId);
      return { ...prev, [groupId]: updated };
    });
    
    showSuccess(`Pending invitation for ${userName} removed`);
  }, [editingCampfire]);

  const updateCampfire = useCallback(async () => {
    if (!editingCampfire || !editCampfireName.trim() || !editCampfireDescription.trim()) {
      showError("Name and description are required");
      return;
    }

    setIsUpdatingCampfire(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${getApiUrl("/api/connect/conversations")}/${editingCampfire.id}`,
        {
          name: editCampfireName.trim(),
          description: editCampfireDescription.trim(),
          isPublic: false // Force private campfires only
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Campfire updated successfully! 🔥");
      setShowEditCampfireModal(false);
      setEditingCampfire(null);
      
      // Refresh conversations
      await fetchConversations();
    } catch (err) {
      console.error("Error updating campfire:", err);
      showError(err.response?.data?.error || "Failed to update campfire");
    } finally {
      setIsUpdatingCampfire(false);
    }
  }, [editingCampfire, editCampfireName, editCampfireDescription, fetchConversations]);

  const openManageParticipantsModal = useCallback((campfire) => {
    // Only owners and admins can manage participants
    if (campfire.userRole !== 'owner' && campfire.userRole !== 'admin') {
      showError("You don't have permission to manage participants");
      return;
    }
    
    setManagingCampfire(campfire);
    fetchGroupParticipants(campfire.id);
    setShowManageParticipantsModal(true);
  }, [fetchGroupParticipants]);

  const removeParticipant = useCallback(async (participantId, participantName) => {
    if (!managingCampfire) return;
    
    // Additional permission checks
    if (managingCampfire.userRole === 'admin') {
      // Admins can't remove owners
      const participant = groupParticipants.find(p => p.id === participantId);
      if (participant && participant.role === 'owner') {
        showError("Admins cannot remove campfire owners");
        return;
      }
    }
    
    // Owners can't remove themselves (they should delete the campfire instead)
    if (managingCampfire.userRole === 'owner' && participantId === localStorage.getItem('userId')) {
      showError("Campfire owners cannot remove themselves. Use the delete button instead.");
      return;
    }
    
    setParticipantToRemove(participantId);
    setIsRemovingParticipant(true);
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${managingCampfire.id}/remove-participant`,
        { userId: participantId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess(`${participantName} has been removed from the campfire`);
      
      // Refresh participants list
      await fetchGroupParticipants(managingCampfire.id);
      
      // Refresh conversations if this affects the current user
      if (participantId === localStorage.getItem('userId')) {
        await fetchConversations();
      }
    } catch (err) {
      console.error("Error removing participant:", err);
      showError(err.response?.data?.error || "Failed to remove participant");
    } finally {
      setIsRemovingParticipant(false);
      setParticipantToRemove(null);
    }
  }, [managingCampfire, fetchGroupParticipants, fetchConversations, groupParticipants]);

  const fetchConnectionRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/connect/requests"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setConnectionRequests(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching connection requests:", err);
      throw err;
    }
  }, []);

  const fetchSentConnectionRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/connect/requests/sent"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSentConnectionRequests(response.data);
      return response.data;
    } catch (err) {
      console.error("Error fetching sent connection requests:", err);
      throw err;
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId) => {
    try {
      setIsRefreshingMessages(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${getApiUrl("/api/connect/conversations")}/${conversationId}/messages`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      // Determine prior count for this conversation
      const priorCount = lastMessageCountByConversation[conversationId] || 0;
      const isInitialLoadOfThisConversation = lastLoadedConversationIdRef.current !== conversationId || priorCount === 0;

      // Check if there are new messages (suppress on initial load when switching conversations)
      if (!isInitialLoadOfThisConversation && response.data.length > priorCount) {
        const newMessages = response.data.slice(priorCount);
        const incomingMessages = newMessages.filter(msg => !msg.isOwn);
        if (incomingMessages.length > 0) {
          showSuccess(`You have ${incomingMessages.length} new message${incomingMessages.length > 1 ? 's' : ''}!`);
          playNotificationSound();
        }
      }
      
      setMessages(response.data);
      setLastMessageCountByConversation(prev => ({ ...prev, [conversationId]: response.data.length }));
      lastLoadedConversationIdRef.current = conversationId;
      
      // Scroll to bottom instantly when first loading messages
      // This ensures the conversation starts at the most recent message without slow smooth scrolling
      setTimeout(() => scrollToBottom(true), 100);
      
      // Mark all unread messages in this conversation as read
      const unreadMessages = response.data.filter(msg => !msg.isOwn && msg.unread);
      if (unreadMessages.length > 0) {
        await Promise.all(
          unreadMessages.map(message => 
            axios.put(`${getApiUrl("/api/connect/messages")}/${message.id}/read`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            })
          )
        );
        
        // Refresh the unread count in the sidebar and notify all components
        await refreshCountAndNotify();
        
        // Refresh the conversations list to update unread counts
        await fetchConversations();
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    } finally {
      setIsRefreshingMessages(false);
    }
  }, [lastMessageCountByConversation, refreshCount, fetchConversations, scrollToBottom]);

  // Auto-expand textarea functionality
  const handleTextareaChange = useCallback((e) => {
    setNewMessage(e.target.value);
    
    // Auto-expand textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 150; // Maximum height in pixels
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  }, []);

  const sendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${selectedConversation.id}/messages`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add the new message to the local state immediately
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (err) {
      console.error("Error sending message:", err);
      showError("Failed to send message");
    }
  }, [newMessage, selectedConversation]);

  const sendConnectionRequest = useCallback(async () => {
    if (!selectedUser || !requestMessage.trim()) return;
    
    setIsSendingRequest(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/requests")}/${selectedUser.id}`,
        { message: requestMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Connection request sent successfully!");
      setShowRequestModal(false);
      setSelectedUser(null);
      setRequestMessage("");
      // Refresh users with current search term to maintain search results
      fetchUsers(1, searchTerm);
      fetchSentConnectionRequests(); // Refresh sent requests
    } catch (err) {
      console.error("Error sending connection request:", err);
      showError(err.response?.data?.error || "Failed to send connection request");
    } finally {
      setIsSendingRequest(false);
    }
  }, [selectedUser, requestMessage, searchTerm, fetchUsers, fetchSentConnectionRequests]);

  const acceptConnectionRequest = useCallback(async (requestId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/requests")}/${requestId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Connection request accepted!");
      fetchConnectionRequests();
      fetchConversations();
      // Refresh users to update connection status
      fetchUsers(1, searchTerm);
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      console.error("Error accepting connection request:", err);
      showError("Failed to accept connection request");
    }
  }, [fetchConnectionRequests, fetchConversations, searchTerm, fetchUsers, refreshNotifications]);

  const declineConnectionRequest = useCallback(async (requestId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${getApiUrl("/api/connect/requests")}/${requestId}/decline`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Connection request declined");
      fetchConnectionRequests();
      // Refresh users to update connection status
      fetchUsers(1, searchTerm);
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      console.error("Error declining connection request:", err);
      showError("Failed to decline connection request");
    }
  }, [fetchConnectionRequests, searchTerm, fetchUsers, refreshNotifications]);

  const cancelConnectionRequest = useCallback(async (requestId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${getApiUrl("/api/connect/requests")}/${requestId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Connection request cancelled");
      // Refresh users with current search term to maintain search results
      fetchUsers(1, searchTerm);
      fetchSentConnectionRequests(); // Refresh sent requests
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      console.error("Error cancelling connection request:", err);
      showError("Failed to cancel connection request");
    }
  }, [searchTerm, fetchUsers, fetchSentConnectionRequests, refreshNotifications]);

  const getConnectionStatusBadge = useCallback((status) => {
    switch (status) {
      case 'connected':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Connected</span>;
      case 'request_sent':
        return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">Request Sent</span>;
      case 'request_received':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">Request Received</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">Not Connected</span>;
    }
  }, []);

  const getConnectionButton = useCallback((user) => {
    switch (user.connection_status) {
      case 'connected':
        return (
          <button
            onClick={() => {
              // Find existing conversation and switch to messages tab
              const conversation = conversations.find(c => c.participant.id === user.id);
              if (conversation) {
                setSelectedConversation(conversation);
                setActiveTab("messages");
              }
            }}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
          >
            <MessageCircle size={16} />
            <span>Message</span>
          </button>
        );
      case 'request_sent':
        return (
          <button
            onClick={() => cancelConnectionRequest(user.request_id)}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
          >
            <Clock size={16} />
            <span>Cancel Request</span>
          </button>
        );
      case 'request_received':
        return (
          <div className="space-y-2">
            <button
              onClick={() => acceptConnectionRequest(user.request_id)}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Check size={16} />
              <span>Accept</span>
            </button>
            <button
              onClick={() => declineConnectionRequest(user.request_id)}
              className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
            >
              <X size={16} />
              <span>Decline</span>
            </button>
          </div>
        );
      default:
        return (
          <button
            onClick={() => {
              setSelectedUser(user);
              setShowRequestModal(true);
            }}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
          >
            <UserPlus size={16} />
            <span>Connect</span>
          </button>
        );
    }
  }, [conversations, cancelConnectionRequest, acceptConnectionRequest, declineConnectionRequest]);

  // Contact sharing functions
  const addSharedContact = useCallback(async (contactData, type) => {
    try {
      setIsAddingContact(true);
      const token = localStorage.getItem("token");
      
      // Parse contactData if it's a string
      let parsedContactData = contactData;
      if (typeof contactData === 'string') {
        try {
          parsedContactData = JSON.parse(contactData);
        } catch (parseError) {
          console.error('Error parsing contactData:', parseError);
          showError('Invalid contact data format');
          return;
        }
      }
      
      if (type === 'veterinarian') {
        // Map the fields to match what the backend expects, only including defined values
        const vetData = {};
        if (parsedContactData.name) vetData.name = parsedContactData.name;
        if (parsedContactData.clinic_name) vetData.clinic_name = parsedContactData.clinic_name;
        if (parsedContactData.phone) vetData.phone = parsedContactData.phone;
        if (parsedContactData.email) vetData.email = parsedContactData.email;
        if (parsedContactData.address) vetData.address = parsedContactData.address;
        if (parsedContactData.specialties) vetData.specialties = parsedContactData.specialties;
        if (parsedContactData.notes) vetData.notes = parsedContactData.notes;
        
        const response = await axios.post(getApiUrl('/api/veterinarians'), vetData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200 || response.status === 201) {
          showSuccess("Veterinarian added to your contacts successfully! 🏥");
        }
      } else if (type === 'farrier') {
        // Map the fields to match what the backend expects, only including defined values
        const farrierData = {};
        if (parsedContactData.name) farrierData.name = parsedContactData.name;
        if (parsedContactData.clinic_name) farrierData.clinic_name = parsedContactData.clinic_name;
        if (parsedContactData.phone) farrierData.phone = parsedContactData.phone;
        if (parsedContactData.email) farrierData.email = parsedContactData.email;
        if (parsedContactData.address) farrierData.address = parsedContactData.address;
        if (parsedContactData.specialties) farrierData.specialties = parsedContactData.specialties;
        if (parsedContactData.notes) farrierData.notes = parsedContactData.notes;
        
        const response = await axios.post(getApiUrl('/api/farriers'), farrierData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200 || response.status === 201) {
          showSuccess("Farrier added to your contacts successfully! 🔨");
        }
      } else if (type === 'event') {
        // Map the fields to match what the backend expects, only including defined values
        const eventData = {};
        if (parsedContactData.name) eventData.name = parsedContactData.name;
        if (parsedContactData.event_date) eventData.event_date = parsedContactData.event_date;
        if (parsedContactData.start_time) eventData.start_time = parsedContactData.start_time;
        if (parsedContactData.duration_minutes) eventData.duration_minutes = parsedContactData.duration_minutes;
        if (parsedContactData.description) eventData.description = parsedContactData.description;
        if (parsedContactData.location) eventData.location = parsedContactData.location;
        if (parsedContactData.event_type) eventData.event_type = parsedContactData.event_type;
        if (parsedContactData.is_all_day !== undefined) eventData.is_all_day = parsedContactData.is_all_day;
        
        const response = await axios.post(getApiUrl('/api/calendar/events'), eventData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.status === 200 || response.status === 201) {
          showSuccess("Event added to your calendar successfully! 📅");
        }
      }
      
      setShowAddContactModal(false);
      setContactToAdd(null);
      setContactType('');
    } catch (error) {
      console.error('Error adding shared contact:', error);
      console.error('Error response:', error.response?.data);
      showError(`Failed to add contact: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsAddingContact(false);
    }
  }, [showSuccess, showError]);

  const sendContactShareMessage = useCallback(async (contactData, contactType) => {
    if (!selectedConversation) return;

    try {
      setIsSharingContact(true);
      const token = localStorage.getItem("token");
      
      
      const messageData = {
        type: contactType === 'veterinarian' ? 'vet_share' : contactType === 'farrier' ? 'farrier_share' : 'event_share',
        content: contactData
      };
      
      const response = await axios.post(
        `${getApiUrl("/api/connect/conversations")}/${selectedConversation.id}/messages`,
        messageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add the new message to the local state immediately
      setMessages(prev => [...prev, response.data]);
      
      // Show success message
      const typeLabel = contactType === 'veterinarian' ? 'Veterinarian' : 
                       contactType === 'farrier' ? 'Farrier' : 
                       contactType === 'event' ? 'Event' : 'Contact';
      showSuccess(`${typeLabel} shared successfully!`);
    } catch (err) {
      console.error("Error sending contact share message:", err);
      showError("Failed to share contact");
    } finally {
      setIsSharingContact(false);
    }
  }, [selectedConversation, showSuccess, showError]);

  const handleVetSelection = useCallback((vet) => {
    setSelectedVet(vet);
    // Send the vet as a contact share message
    sendContactShareMessage(vet, 'veterinarian');
    setShowVetModal(false);
  }, [sendContactShareMessage]);

  const handleFarrierSelection = useCallback((farrier) => {
    setSelectedFarrier(farrier);
    // Send the farrier as a contact share message
    sendContactShareMessage(farrier, 'farrier');
    setShowFarrierModal(false);
  }, [sendContactShareMessage]);

  const handleEventSelection = useCallback((event) => {
    setSelectedEvent(event);
    // Send the event as a contact share message
    sendContactShareMessage(event, 'event');
    setShowEventModal(false);
  }, [sendContactShareMessage]);


  const openAddContactModal = useCallback((contactData, type) => {
    setContactToAdd(contactData);
    setContactType(type);
    setShowAddContactModal(true);
  }, []);

  // Initial data loading effect
  useEffect(() => {
    if (!isLoading && conversations.length === 0) {
      fetchConversations();
      fetchConnectionRequests();
      fetchSentConnectionRequests();
      fetchPendingInvitations();
    }
  }, [isLoading, conversations.length, fetchConversations, fetchConnectionRequests, fetchSentConnectionRequests, fetchPendingInvitations]);

  // Group conversation functions
  const createCampfire = useCallback(async () => {
    if (!newCampfireName.trim() || !newCampfireDescription.trim()) {
      showError("Name and description are required");
      return;
    }

    setIsCreatingCampfire(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${getApiUrl("/api/connect/conversations/group")}`,
        {
          name: newCampfireName.trim(),
          description: newCampfireDescription.trim(),
          isPublic: false, // Force private campfires only
          theme: 'campfire'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSuccess("Private campfire created successfully! 🔥");
      
      // Send invitations to users in the invite list
      if (usersToInvite.length > 0) {
        for (const user of usersToInvite) {
          try {
            await axios.post(
              `${getApiUrl("/api/connect/conversations")}/${response.data.id}/invite`,
              {
                userId: user.id,
                message: `You're invited to join ${newCampfireName}!`
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (err) {
            console.error(`Error sending invite to ${user.name}:`, err);
          }
        }
        showSuccess(`Invitations sent to ${usersToInvite.length} user(s)! 🔥`);
      }
      
      setShowCreateCampfireModal(false);
      setNewCampfireName("");
      setNewCampfireDescription("");
      setUsersToInvite([]);
      
      // Refresh conversations and switch to messages tab
      await fetchConversations();
      setActiveTab("messages");
      refreshNotifications(); // Refresh sidebar notification count
    } catch (err) {
      console.error("Error creating campfire:", err);
      showError(err.response?.data?.error || "Failed to create campfire");
    } finally {
      setIsCreatingCampfire(false);
    }
  }, [newCampfireName, newCampfireDescription, usersToInvite, fetchConversations, refreshNotifications]);



  // Populate edit campfire form fields when editingCampfire changes
  useEffect(() => {
    if (editingCampfire) {
      setEditCampfireName(editingCampfire.name || '');
      setEditCampfireDescription(editingCampfire.description || '');
    }
  }, [editingCampfire]);

  // Note: Participant search is now manual (like invite system) - no automatic search on typing

  // Prevent body scroll when in conversation view
  useEffect(() => {
    if (selectedConversation) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedConversation]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={selectedConversation ? "fixed top-16 left-0 right-0 bottom-0 lg:left-64 z-30 flex flex-col overflow-hidden" : "h-screen flex flex-col overflow-hidden"}>
      <div className={`bg-white ${selectedConversation ? '' : 'rounded-lg shadow-md'} flex-1 flex flex-col overflow-hidden`}>
        {/* Header - Always Fixed */}
        <div className="border-b border-gray-200 p-2 sm:p-6 flex-shrink-0">
          <h1 className="text-lg sm:text-2xl font-bold text-black-900 flex items-center">
            <LinkIcon className="mr-1 sm:mr-2 h-5 w-5 sm:h-8 sm:w-8 text-black" />
            Connect
          </h1>
          <p className="text-kci-600 mt-2 hidden sm:block">
            Connect with other MyTrailer enthusiasts, share contacts, and join campfire conversations
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
          {selectedConversation ? (
            /* Conversation View - Fixed Headers, Scrollable Messages */
            <div className="h-full flex flex-col overflow-hidden">
              {selectedConversation && (
              <>
                {/* Message Header - Fixed */}
                <div className="p-2 sm:p-4 border-b border-gray-200 bg-white flex-shrink-0">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {/* Back button */}
                    <button
                      onClick={() => setSelectedConversation(null)}
                      className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Back to conversations"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${
                      selectedConversation.isGroup ? 'border-2 border-orange-500 text-orange-500 text-lg sm:text-xl' : 'bg-blue-500 text-white'
                    }`}>
                      {selectedConversation.isGroup ? '🔥' : selectedConversation.participant.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                        {selectedConversation.isGroup ? selectedConversation.name : selectedConversation.participant.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {selectedConversation.isGroup ? (
                          `${selectedConversation.participantCount} members`
                        ) : '' }
                      </p>
                    </div>
                    <button
                      onClick={() => fetchMessages(selectedConversation.id)}
                      disabled={isRefreshingMessages}
                      className="p-1 sm:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Refresh messages"
                    >
                      {isRefreshingMessages ? (
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-gray-600"></div>
                      ) : (
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Messages - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 pb-32 space-y-4 bg-gray-50">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.isOwn
                            ? "bg-blue-100 text-gray-900"
                            : "bg-gray-200 text-gray-900"
                        }`}
                      >
                        {/* Show sender name for group conversations */}
                        {selectedConversation?.isGroup && !message.isOwn && message.user_name && (
                          <p className="text-xs font-medium text-gray-600 mb-1">
                            {message.user_name}
                          </p>
                        )}
                        {message.type === "text" && (
                          <p>{message.content}</p>
                        )}
                        {message.type === "system" && (
                          <p className="italic">{message.content}</p>
                        )}
                        {message.type === "stream_notification" && (
                          <div className="bg-gradient-to-r from-orange-100 to-red-100 p-3 rounded-lg border border-orange-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                  <span className="text-orange-600 text-lg">🔥</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm text-orange-800">Live Stream Started!</p>
                                  <p className="text-sm font-medium text-orange-700">{message.metadata?.streamTitle || 'Live Stream'}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2 mb-3">
                              <div className="bg-white bg-opacity-50 p-2 rounded border">
                                <p className="text-xs font-medium text-gray-700 mb-1">Stream Code:</p>
                                <p className="text-lg font-mono font-bold text-orange-800 bg-white p-2 rounded border">
                                  {message.metadata?.accessCode || 'N/A'}
                                </p>
                              </div>
                              {message.metadata?.broadcasterName && (
                                <p className="text-xs text-orange-600">👤 Started by: {message.metadata.broadcasterName}</p>
                              )}
                            </div>
                            
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  // Auto-join the stream
                                  const streamId = message.metadata?.streamId;
                                  const accessCode = message.metadata?.accessCode;
                                  if (streamId && accessCode) {
                                    // Navigate to fan view page with auto-join
                                    window.location.href = `/streaming/fan-view?join=${streamId}&code=${accessCode}`;
                                  }
                                }}
                                className="flex-1 px-3 py-2 bg-orange-500 text-white text-xs rounded-md hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <span>🎥</span>
                                Join Stream
                              </button>
                              <button
                                onClick={() => {
                                  // Copy code to clipboard
                                  const code = message.metadata?.accessCode;
                                  if (code) {
                                    navigator.clipboard.writeText(code).then(() => {
                                      showSuccess('Stream code copied to clipboard!');
                                    }).catch(() => {
                                      showError('Failed to copy stream code');
                                    });
                                  }
                                }}
                                className="px-3 py-2 bg-orange-200 text-orange-800 text-xs rounded-md hover:bg-orange-300 transition-colors flex items-center justify-center gap-2 font-medium"
                              >
                                <span>📋</span>
                                Copy
                              </button>
                            </div>
                          </div>
                        )}
                        {message.type === "vet_share" && (
                          <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                  <span className="text-green-600 text-lg">🏥</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">Shared Veterinarian</p>
                                  <p className="text-sm font-medium">{message.content.name}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-1 mb-3">
                              {message.content.clinic_name && (
                                <p className="text-xs opacity-80">🏢 {message.content.clinic_name}</p>
                              )}
                              {message.content.phone && (
                                <p className="text-xs opacity-80">📞 {message.content.phone}</p>
                              )}
                              {message.content.email && (
                                <p className="text-xs opacity-80">✉️ {message.content.email}</p>
                              )}
                              {message.content.address && (
                                <p className="text-xs opacity-80">📍 {message.content.address}</p>
                              )}
                              {message.content.specialties && (
                                <p className="text-xs opacity-80">🎯 {message.content.specialties}</p>
                              )}
                              {message.content.notes && (
                                <p className="text-xs opacity-80">📝 {message.content.notes}</p>
                              )}
                            </div>
                            
                            <button
                              onClick={() => addSharedContact(message.content, 'veterinarian')}
                              className="w-full px-3 py-2 bg-green-200 text-black text-xs rounded-md hover:bg-green-300 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>➕</span>
                              Add to My Vets
                            </button>
                          </div>
                        )}
                        {message.type === "farrier_share" && (
                          <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <span className="text-purple-600 text-lg">🔨</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">Shared Farrier</p>
                                  <p className="text-sm font-medium">{message.content.name}</p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-1 mb-3">
                              {message.content.clinic_name && (
                                <p className="text-xs opacity-80">🏢 {message.content.clinic_name}</p>
                              )}
                              {message.content.phone && (
                                <p className="text-xs opacity-80">📞 {message.content.phone}</p>
                              )}
                              {message.content.email && (
                                <p className="text-xs opacity-80">✉️ {message.content.email}</p>
                              )}
                              {message.content.address && (
                                <p className="text-xs opacity-80">📍 {message.content.address}</p>
                              )}
                              {message.content.specialties && (
                                <p className="text-xs opacity-80">🎯 {message.content.specialties}</p>
                              )}
                              {message.content.notes && (
                                <p className="text-xs opacity-80">📝 {message.content.notes}</p>
                              )}
                            </div>
                            
                            <button
                              onClick={() => addSharedContact(message.content, 'farrier')}
                              className="w-full px-3 py-2 bg-purple-200 text-black text-xs rounded-md hover:bg-purple-300 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>➕</span>
                              Add to My Farriers
                            </button>
                          </div>
                        )}
                        {message.type === "event_share" && (
                          <div className="bg-white bg-opacity-10 p-3 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                                  <span className="text-orange-600 text-lg">📅</span>
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">Shared Event</p>
                                  <p className="text-sm font-medium">
                                    {(() => {
                                      let eventData = message.content;
                                      if (typeof message.content === 'string') {
                                        try {
                                          eventData = JSON.parse(message.content);
                                        } catch (e) {
                                          eventData = {};
                                        }
                                      }
                                      return eventData.name || eventData.title || 'Event';
                                    })()}
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-1 mb-3">
                              {(() => {
                                // Parse the JSON string if it's a string
                                let eventData = message.content;
                                if (typeof message.content === 'string') {
                                  try {
                                    eventData = JSON.parse(message.content);
                                  } catch (e) {
                                    eventData = {};
                                  }
                                }
                                
                                return (
                                  <>
                                    {eventData.event_date && (
                                      <p className="text-xs opacity-80">📅 {formatDate(eventData.event_date)}</p>
                                    )}
                                    {eventData.start_time && (
                                      <p className="text-xs opacity-80">🕐 {eventData.start_time}</p>
                                    )}
                                    {eventData.location && eventData.location.trim() && (
                                      <p className="text-xs opacity-80">📍 {eventData.location}</p>
                                    )}
                                    {eventData.description && eventData.description.trim() && (
                                      <p className="text-xs opacity-80">📝 {eventData.description}</p>
                                    )}
                                    {eventData.event_type && (
                                      <p className="text-xs opacity-80">🏷️ {eventData.event_type}</p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            
                            <button
                              onClick={() => addSharedContact(message.content, 'event')}
                              className="w-full px-3 py-2 bg-orange-200 text-black text-xs rounded-md hover:bg-orange-300 transition-colors flex items-center justify-center gap-2"
                            >
                              <span>➕</span>
                              Add to My Events
                            </button>
                          </div>
                        )}
                        {message.type === "horse_share" && (
                          <div className="bg-white bg-opacity-10 p-2 rounded">
                            <p className="font-semibold">Shared Horse</p>
                            <p>{message.content.name}</p>
                            <p className="text-sm">{message.content.breed}</p>
                            <p className="text-sm">Age: {message.content.age}</p>
                          </div>
                        )}
                        {message.type === "calendar_share" && (
                          <div className="bg-white bg-opacity-10 p-2 rounded">
                            <p className="font-semibold">Shared Event</p>
                            <p>{message.content.title}</p>
                            <p className="text-sm">{message.content.date}</p>
                            <p className="text-sm">{message.content.description}</p>
                          </div>
                        )}
                        <p className="text-xs mt-1 opacity-70">
                          {new Date(message.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Invisible element for auto-scrolling */}
                  <div ref={messagesEndRef} />
                </div>
              </>
              )}
            </div>
          ) : (
            /* Non-conversation content - scrollable */
            <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
              <div className="pt-4 px-4 pb-1 sm:pt-6 sm:px-6 sm:pb-1">
                {activeTab === "discover" && (
            <div className="h-full overflow-y-auto">
              {/* Enhanced Search Section */}
              <div className="mb-6 space-y-4">
                <div className="relative flex flex-col sm:flex-row">
                  <div className="relative flex-1 mb-2 sm:mb-0">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      placeholder="Search by exact email address..."
                      value={searchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSearchTerm(value);
                        // If user clears the input, reset search state
                        if (!value.trim()) {
                          setHasSearched(false);
                          setUsers([]);
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md sm:rounded-l-md sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={!searchTerm.trim()} 
                    className="px-6 py-2 bg-blue-600 text-white border border-blue-600 rounded-md sm:rounded-l-none sm:rounded-r-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Search
                  </button>
                </div>
                
                {/* Search Instructions */}
                <div className="text-xs sm:text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p>🔒 <strong>Privacy-First Search:</strong> Enter the exact email address of the person you want to connect with. Partial searches are not allowed to protect user privacy.</p>
                </div>
              </div>

              {isLoadingUsers ? (
                <div className="flex justify-center items-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <>
                  {/* Search Results */}
                  {searchTerm && (
                    <div className="space-y-4">                     
                      {users.length > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                          <p className="text-green-800 text-sm">
                            ✅ Found user{users.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      )}
                      
                      {/* Detailed User Cards (only shown after search) */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        {users.map((user) => (
                          <div key={user.id} className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
                            <div className="flex items-center space-x-3 sm:space-x-4 mb-4">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{user.name}</h3>
                                <p className="text-xs sm:text-sm text-gray-600">{user.city}, {user.state}</p>
                                <div className="mt-1">
                                  {getConnectionStatusBadge(user.connection_status)}
                                </div>
                              </div>
                            </div>
                            
                            <div className="mb-4">
                              <p className="text-xs sm:text-sm text-gray-600">
                                <span className="font-medium">Location:</span> {user.city}, {user.state}
                              </p>
                            </div>

                            {getConnectionButton(user)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="flex justify-center items-center space-x-2">
                      <button
                        onClick={() => fetchUsers(pagination.currentPage - 1, searchTerm)}
                        disabled={!pagination.hasPrevPage}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      <span className="px-3 py-2 text-sm text-gray-700">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      
                      <button
                        onClick={() => fetchUsers(pagination.currentPage + 1, searchTerm)}
                        disabled={!pagination.hasNextPage}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}

                  {users.length === 0 && searchTerm && hasSearched && (
                    <div className="text-center py-8 sm:py-12">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 sm:p-6 max-w-md mx-auto">
                        <p className="text-yellow-800 font-medium mb-2 text-sm sm:text-base">No exact match found</p>
                        <p className="text-yellow-700 text-xs sm:text-sm mb-3">
                          No user found with the exact name "{searchTerm}". Please check:
                        </p>
                        <ul className="text-yellow-700 text-xs sm:text-sm text-left space-y-1">
                          <li>• The spelling is correct</li>
                          <li>• You're using the full name</li>
                          <li>• The person is registered on the platform</li>
                        </ul>
                        <button
                          onClick={clearSearch}
                          className="mt-4 px-3 sm:px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-xs sm:text-sm"
                        >
                          Clear Search
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === "requests" && (
            <div className="h-full overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Connection Requests</h2>
              
              {/* Received Requests Section */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <UserPlus size={20} className="mr-2" />
                  Requests Received ({connectionRequests.length})
                </h3>
                
                {connectionRequests.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <UserPlus className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No pending requests</p>
                    <p className="mt-1 text-sm text-gray-500">You don't have any pending connection requests.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {connectionRequests.map((request) => (
                      <div key={request.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {request.sender_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{request.sender_name}</h3>
                            <p className="text-sm text-gray-600">{request.sender_city}, {request.sender_state}</p>
                            <p className="text-sm text-gray-500">
                              Sent {formatDate(request.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {request.message && (
                          <div className="mb-4 p-3 bg-white rounded border">
                            <p className="text-sm text-gray-700">{request.message}</p>
                          </div>
                        )}
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => acceptConnectionRequest(request.id)}
                            className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <Check size={16} />
                            <span>Accept</span>
                          </button>
                          <button
                            onClick={() => declineConnectionRequest(request.id)}
                            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <X size={16} />
                            <span>Decline</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sent Requests Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Clock size={20} className="mr-2" />
                  Requests Sent ({sentConnectionRequests.length})
                </h3>
                
                {sentConnectionRequests.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Clock className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm font-medium text-gray-900">No sent requests</p>
                    <p className="mt-1 text-sm text-gray-500">You haven't sent any connection requests yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sentConnectionRequests.map((request) => (
                      <div key={request.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {request.receiver_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900">{request.receiver_name}</h3>
                            <p className="text-sm text-gray-600">{request.receiver_city}, {request.receiver_state}</p>
                            <p className="text-sm text-gray-500">
                              Sent {formatDate(request.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        {request.message && (
                          <div className="mb-4 p-3 bg-white rounded border">
                            <p className="text-sm text-gray-700">{request.message}</p>
                          </div>
                        )}
                        
                        <div className="flex justify-end">
                          <button
                            onClick={() => cancelConnectionRequest(request.id)}
                            className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                          >
                            <X size={16} />
                            <span>Cancel Request</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "invitations" && (
            <div className="h-full overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                📨 Campfire Invitations
              </h2>
              
              {(() => {
                return null;
              })()}
              
              {!pendingInvitations || !Array.isArray(pendingInvitations) || pendingInvitations.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <div className="text-4xl mb-4">📨</div>
                  <p className="mt-2 text-sm font-medium text-gray-900">No pending invitations</p>
                  <p className="mt-1 text-sm text-gray-500">You don't have any pending campfire invitations.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingInvitations.map((invitation) => (
                    <div key={invitation.id} className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-6 border border-green-200 shadow-sm">
                      <div className="flex items-center space-x-4 mb-4">
                        <div className="text-2xl">🔥</div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{invitation.campfire_name}</h3>
                          <p className="text-sm text-gray-600">{invitation.campfire_description}</p>
                          <p className="text-sm text-gray-500">
                            Invited by {invitation.inviter_name} • {formatDate(invitation.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      {invitation.message && (
                        <div className="mb-4 p-3 bg-white rounded border">
                          <p className="text-sm text-gray-700">{invitation.message}</p>
                        </div>
                      )}
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => acceptInvitation(invitation.id)}
                          className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <Check size={16} />
                          <span>Accept Invitation</span>
                        </button>
                        <button
                          onClick={() => declineInvitation(invitation.id)}
                          className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
                        >
                          <X size={16} />
                          <span>Decline</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "campfires" && (
            <div className="h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  Campfires
                </h2>
                <button
                  onClick={() => setShowCreateCampfireModal(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <svg className="hidden sm:block w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="hidden sm:inline">Create Campfire</span>
                  <span className="sm:hidden">+</span>
                </button>
              </div>

              {/* My Campfires */}
              <div>
                <h3 className="text-lg font-medium text-kci-900 mb-4 flex items-center">
                  🔥 My Campfires
                </h3>
                
                {isLoading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rodeo-600"></div>
                  </div>
                ) : conversations.filter(c => c.isGroup && c.userRole === 'owner' && !c.isPublic).length === 0 ? (
                  <div className="text-center py-8 bg-kci-50 rounded-lg">
                    <div className="text-4xl mb-4">🔥</div>
                    <p className="mt-2 text-sm font-medium text-kci-900">No campfires yet</p>
                    <p className="mt-1 text-sm text-kci-500">Create your first private campfire!</p>
                    <button
                      onClick={() => setShowCreateCampfireModal(true)}
                      className="mt-4 bg-rodeo-600 text-white px-4 py-2 rounded-md hover:bg-rodeo-700 transition-colors text-sm"
                    >
                      Create Campfire
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Mobile Layout - Stacked Cards */}
                    <div className="md:hidden">
                      {conversations.filter(c => c.isGroup && c.userRole === 'owner' && !c.isPublic).map((group) => (
                        <div key={group.id} className="bg-gradient-to-br from-kci-50 to-earth-50 rounded-lg p-4 border border-kci-200 shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xl">🔥</div>
                            <span className="text-xs bg-rodeo-100 text-rodeo-800 px-2 py-1 rounded-full">
                              {group.participantCount}
                            </span>
                          </div>
                          
                          <h3 className="font-semibold text-kci-900 mb-1 text-sm">{group.name}</h3>
                          <p className="text-xs text-kci-600 mb-2">{group.description}</p>
                          
                          <div className="text-xs text-kci-500 mb-3">
                            <p>Theme: {group.theme}</p>
                            <p>Created {formatDate(group.created_at)}</p>
                          </div>
                          
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openEditCampfireModal(group)}
                              className="p-2 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 border border-blue-200"
                              aria-label="Edit"
                              title="Edit campfire"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => confirmDeleteCampfire(group)}
                              className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-50 border border-red-200"
                              aria-label="Delete"
                              title="Delete campfire"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Layout - Compact Table-like Rows */}
                    <div className="hidden md:block">
                      <div className="bg-white rounded-lg border border-kci-200 overflow-hidden">
                        {/* Header */}
                        <div className="bg-kci-50 px-4 py-3 border-b border-kci-200">
                          <div className="grid grid-cols-12 gap-3 text-xs font-medium text-kci-700">
                            <div className="col-span-3">Campfire</div>
                            <div className="col-span-3">Description</div>
                            <div className="col-span-2">Members</div>
                            <div className="col-span-1">Pending</div>
                            <div className="col-span-1">Created</div>
                            <div className="col-span-2">Actions</div>
                          </div>
                        </div>
                        
                        {/* Rows */}
                        {conversations.filter(c => c.isGroup && c.userRole === 'owner' && !c.isPublic).map((group) => (
                          <div key={group.id} className="px-4 py-3 border-b border-kci-100 hover:bg-kci-50 transition-colors">
                            <div className="grid grid-cols-12 gap-3 items-center">
                              {/* Campfire Name */}
                              <div className="col-span-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-lg">🔥</span>
                                  <div>
                                    <h3 className="font-semibold text-kci-900 text-sm">{group.name}</h3>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Description */}
                              <div className="col-span-3">
                                <p className="text-sm text-kci-600 truncate" title={group.description}>
                                  {group.description}
                                </p>
                              </div>
                              
                              {/* Members */}
                              <div className="col-span-2">
                                <span className="text-xs bg-rodeo-100 text-rodeo-800 px-2 py-1 rounded-full">
                                  {group.participantCount}
                                </span>
                              </div>
                              
                              {/* Pending Invitations */}
                              <div className="col-span-1">
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                  {(pendingInvitesByGroup[group.id] || []).length}
                                </span>
                              </div>
                              
                              {/* Created Date */}
                              <div className="col-span-1">
                                <span className="text-xs text-kci-600">
                                  {formatDate(group.created_at)}
                                </span>
                              </div>
                              
                              {/* Actions */}
                              <div className="col-span-2">
                                <div className="flex flex-wrap gap-1">
                                  <button
                                    onClick={() => openEditCampfireModal(group)}
                                    className="px-2 py-1 rounded text-xs text-blue-600 hover:text-blue-900 hover:bg-blue-50 border border-blue-200"
                                    title="Edit campfire"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteCampfire(group)}
                                    className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50 border border-red-200"
                                    title="Delete campfire"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

             </div>
            )}

          {activeTab === "messages" && (
            <div className="h-full flex flex-col overflow-hidden">
              {!selectedConversation ? (
                /* Conversations List View */
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-4 text-sm font-medium text-gray-900">No conversations yet</p>
                      <p className="mt-1 text-xs text-gray-500">Connect with users to start messaging</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {conversations.map((conversation) => (
                        <div
                          key={conversation.id}
                          onClick={() => {
                            setSelectedConversation(conversation);
                            fetchMessages(conversation.id);
                          }}
                          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors active:bg-gray-100"
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold flex-shrink-0 ${
                              conversation.isGroup ? 'border-2 border-orange-500 text-orange-500 text-xl' : 'bg-blue-500 text-white'
                            }`}>
                              {conversation.isGroup ? '🔥' : conversation.participant.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-semibold text-gray-900 truncate">
                                  {conversation.isGroup ? conversation.name : conversation.participant.name}
                                </p>
                                {conversation.unreadCount > 0 && (
                                  <span className="ml-2 bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full min-w-[20px] text-center flex-shrink-0">
                                    {conversation.unreadCount}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 truncate">
                                {formatLastMessageContent(conversation.lastMessage, conversation.isGroup)}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
 
        </div>
              )}
      </div>

      {/* Fixed Message Input and Share Buttons - Only visible when viewing a conversation */}
      {selectedConversation && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-gray-200 lg:left-64">
          <div className="p-4">
            <form onSubmit={sendMessage} className="flex space-x-2 mb-3">
              <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={handleTextareaChange}
                placeholder="Type your message..."
                rows={1}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none overflow-y-auto"
                style={{ minHeight: '40px', maxHeight: '150px' }}
              />
              <button
                type="submit"
                disabled={!newMessage.trim()}
                className="flex-shrink-0 bg-blue-600 text-white px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Send message"
              >
                <Send className="h-5 w-5" />
              </button>
            </form>

            {/* Share Buttons */}
            <div className="flex gap-2">
              <button
                onClick={openVetModal}
                disabled={isSharingContact}
                className="flex-1 py-2 bg-green-100 text-green-700 text-sm font-medium rounded-lg hover:bg-green-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
                <Stethoscope className="h-4 w-4" />
              </button>
              <button
                onClick={openFarrierModal}
                disabled={isSharingContact}
                className="flex-1 py-2 bg-purple-100 text-purple-700 text-sm font-medium rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
                <Hammer className="h-4 w-4" />
              </button>
              <button
                onClick={openEventModal}
                disabled={isSharingContact}
                className="flex-1 py-2 bg-orange-100 text-orange-700 text-sm font-medium rounded-lg hover:bg-orange-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Share2 className="h-4 w-4" />
                Share
                <Calendar className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Buttons - Hide when viewing a conversation */}
      {(activeTab !== "messages" || !selectedConversation) && (
      <div className="bg-white border-t border-gray-200 pt-1 px-2 pb-2 sm:pt-1 sm:px-4 sm:pb-4 flex-shrink-0 fixed bottom-0 left-0 right-0 z-10 lg:left-64">
        <div className="flex justify-around gap-1 sm:gap-2 max-w-6xl mx-auto">
          <button
            onClick={() => {
              setActiveTab("messages");
              // Always fetch conversations if we don't have any, or if we're switching to messages tab
              if (conversations.length === 0 || activeTab !== "messages") {
                fetchConversations();
              }
              // Also refresh the unread count to ensure it's up to date
              refreshCountAndNotify();
            }}
            className={`flex-1 flex flex-col items-center justify-center py-2 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors ${
              activeTab === "messages"
                ? "bg-blue-100 text-blue-600 border-2 border-blue-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <MessageCircle size={18} className="sm:size-6 mb-1" />
            <span className="text-xs font-medium hidden sm:block">Messages</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("campfires");
            }}
            className={`flex-1 flex flex-col items-center justify-center py-2 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors ${
              activeTab === "campfires"
                ? "bg-orange-100 text-orange-600 border-2 border-orange-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="text-lg sm:text-xl mb-1">🔥</div>
            <span className="text-xs font-medium hidden sm:block">Campfires</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("invitations");
              if (pendingInvitations.length === 0) {
                fetchPendingInvitations();
              } else {
              }
            }}
            className={`flex-1 flex flex-col items-center justify-center py-2 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors relative ${
              activeTab === "invitations"
                ? "bg-green-100 text-green-600 border-2 border-green-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <div className="text-lg sm:text-xl mb-1">📨</div>
            <span className="text-xs font-medium hidden sm:block">Campfire Invites</span>
            {pendingInvitations && Array.isArray(pendingInvitations) && pendingInvitations.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                {pendingInvitations.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("discover")}
            className={`flex-1 flex flex-col items-center justify-center py-2 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors ${
              activeTab === "discover"
                ? "bg-blue-100 text-blue-600 border-2 border-blue-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <Users size={18} className="sm:size-6 mb-1" />
            <span className="text-xs font-medium hidden sm:block">Discover</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("requests");
              if (connectionRequests.length === 0 && sentConnectionRequests.length === 0) {
                fetchConnectionRequests();
                fetchSentConnectionRequests();
              }
            }}
            className={`flex-1 flex flex-col items-center justify-center py-2 sm:py-3 px-2 sm:px-4 rounded-lg transition-colors relative ${
              activeTab === "requests"
                ? "bg-blue-100 text-blue-600 border-2 border-blue-300"
                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
            }`}
          >
            <UserPlus size={18} className="sm:size-6 mb-1" />
            <span className="text-xs font-medium hidden sm:block">Requests</span>
            {(connectionRequests.length > 0 || sentConnectionRequests.length > 0) && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                {connectionRequests.length + sentConnectionRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>
      )}

      {/* Connection Request Modal */}
      {showRequestModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {selectedUser.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Connect with {selectedUser.name}</h3>
                  <p className="text-sm text-gray-600">{selectedUser.city}, {selectedUser.state}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (optional)
                </label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Introduce yourself and explain why you'd like to connect..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRequestModal(false);
                    setSelectedUser(null);
                    setRequestMessage("");
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={sendConnectionRequest}
                  disabled={isSendingRequest}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSendingRequest ? "Sending..." : "Send Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Veterinarian Selection Modal */}
      {showVetModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Select Veterinarian to Share</h3>
                <button
                  onClick={() => setShowVetModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-4 max-h-64 overflow-y-auto">
                {isLoadingVets ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading veterinarians...</p>
                  </div>
                ) : veterinarians.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No veterinarians found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {veterinarians.map((vet) => (
                      <div
                        key={vet.id}
                        onClick={() => handleVetSelection(vet)}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-green-300 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{vet.name}</div>
                        <div className="text-sm text-gray-600">{vet.clinic_name}</div>
                        <div className="text-xs text-gray-500">{vet.phone}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowVetModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Farrier Selection Modal */}
      {showFarrierModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Select Farrier to Share</h3>
                <button
                  onClick={() => setShowFarrierModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-4 max-h-64 overflow-y-auto">
                {isLoadingFarriers ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading farriers...</p>
                  </div>
                ) : farriers.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No farriers found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {farriers.map((farrier) => (
                      <div
                        key={farrier.id}
                        onClick={() => handleFarrierSelection(farrier)}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-purple-300 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{farrier.name}</div>
                        <div className="text-sm text-gray-600">{farrier.clinic_name}</div>
                        <div className="text-xs text-gray-500">{farrier.phone}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowFarrierModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Selection Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Select Event to Share</h3>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="mb-4 max-h-64 overflow-y-auto">
                {isLoadingEvents ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading events...</p>
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No events found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {events.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => handleEventSelection(event)}
                        className="p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-orange-300 transition-colors"
                      >
                        <div className="font-medium text-gray-900">{event.name}</div>
                        <div className="text-sm text-gray-600">
                          {formatDate(event.event_date)}
                          {event.start_time && ` at ${event.start_time}`}
                        </div>
                        {event.location && (
                          <div className="text-xs text-gray-500">📍 {event.location}</div>
                        )}
                        {event.event_type && (
                          <div className="text-xs text-gray-500">🏷️ {event.event_type}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowEventModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}




      {/* Invite to Campfire Modal */}
      {showInviteModal && currentInviteGroup && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🔥 Invite to {currentInviteGroup.name}
                </h3>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                {/* Search for users */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search for users to invite
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inviteSearchTerm}
                      onChange={(e) => setInviteSearchTerm(e.target.value)}
                      placeholder="Enter full name"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      onClick={searchUsersForInvite}
                      disabled={!inviteSearchTerm.trim() || isSearchingInvites}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearchingInvites ? "..." : "Search"}
                    </button>
                  </div>
                </div>

                {/* Search results */}
                {inviteSearchResults.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Click on the user to add to the invite list
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {inviteSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => setSelectedInviteUser(user)}
                          className={`p-2 border rounded-md cursor-pointer transition-colors ${
                            selectedInviteUser?.id === user.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'bg-orange-50'
                          }`}
                        >
                          <div className="font-medium text-gray-900">{user.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Invite message */}
                {selectedInviteUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invitation message (optional)
                    </label>
                    <textarea
                      value={inviteMessage}
                      onChange={(e) => setInviteMessage(e.target.value)}
                      placeholder={`You're invited to join ${currentInviteGroup.name}!`}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                )}

                {/* Send invite button */}
                {selectedInviteUser && (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowInviteModal(false)}
                      className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendInvite}
                      disabled={isSendingInvite}
                      className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSendingInvite ? "Sending..." : "Send Invitation"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campfire Modal */}
      {showEditCampfireModal && editingCampfire && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🔥 Edit Campfire
                </h3>
                <button
                  onClick={() => {
                    setShowEditCampfireModal(false);
                    clearSelectedParticipants();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campfire Name *
                  </label>
                  <input
                    type="text"
                    value={editCampfireName}
                    onChange={(e) => setEditCampfireName(e.target.value)}
                    placeholder="e.g., Friday Night Campfire"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={editCampfireDescription}
                    onChange={(e) => setEditCampfireDescription(e.target.value)}
                    placeholder="What's this campfire about?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Members ({(() => {
                      const pending = pendingInvitesByGroup[editingCampfire.id] || [];
                      const ids = new Set(groupParticipants.map(p => p.id));
                      pending.forEach(p => ids.add(p.id));
                      return ids.size;
                    })()})
                  </label>
                  
                  
                  {/* Current Participants List (includes pending invites) */}
                  {(() => {
                    const pending = pendingInvitesByGroup[editingCampfire.id] || [];
                    const byId = new Map(groupParticipants.map(p => [p.id, p]));
                    pending.forEach(p => { if (!byId.has(p.id)) byId.set(p.id, p); });
                    const merged = Array.from(byId.values());
                    return merged.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {merged.map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <span className="text-orange-600 font-medium text-sm">
                                {participant.name ? participant.name.charAt(0).toUpperCase() : '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{participant.name || 'Unknown'}</p>
                              <p className="text-xs text-gray-500">{participant.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {participant.pendingInvite && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">Pending</span>
                            )}
                            {!participant.pendingInvite && participant.role === 'owner' && (
                              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">Owner</span>
                            )}
                            {!participant.pendingInvite && participant.role === 'admin' && (
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Admin</span>
                            )}
                            {!participant.pendingInvite && participant.role === 'member' && (
                              <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">Member</span>
                            )}
                            
                            {/* Remove button - show for non-owners and pending invitations (owners only) */}
                            {editingCampfire?.userRole === 'owner' && (
                              <>
                                {participant.pendingInvite && (
                                  <button
                                    onClick={() => removePendingInvitation(participant.id, participant.name)}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Remove pending invitation"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                                {!participant.pendingInvite && participant.role !== 'owner' && (
                                  <button
                                    onClick={() => confirmRemoveParticipant(participant.id, participant.name)}
                                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                    title="Remove member"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      No members yet
                    </div>
                  );})()}
                </div>

              </div>
              
              <div className="flex space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowEditCampfireModal(false);
                    clearSelectedParticipants();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => openParticipantSearch('edit', editingCampfire)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <UserPlus size={16} />
                  Invite Users
                </button>
                <button
                  onClick={updateCampfire}
                  disabled={isUpdatingCampfire || !editCampfireName.trim() || !editCampfireDescription.trim()}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdatingCampfire ? "Updating..." : "Update Campfire"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Participants Modal */}
      {showManageParticipantsModal && managingCampfire && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  👥 Manage {managingCampfire.name} users
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openInviteModal(managingCampfire)}
                    className="px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 transition-colors"
                  >
                    Invite
                  </button>
                  <button
                    onClick={() => setShowManageParticipantsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              
              <div className="mb-4 max-h-64 overflow-y-auto">
                {groupParticipants.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">No participants found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groupParticipants.map((participant) => (
                      <div key={participant.id} className="p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {participant.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{participant.name}</div>
                              <div className="text-xs text-gray-500">{participant.city}, {participant.state}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              participant.role === 'owner' 
                                ? 'bg-red-100 text-red-800' 
                                : participant.role === 'admin'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {participant.role}
                            </span>
                            {/* Remove button for non-owners */}
                            {(managingCampfire.userRole === 'owner' || (managingCampfire.userRole === 'admin' && participant.role !== 'owner')) && (
                              <button
                                onClick={() => removeParticipant(participant.id, participant.name)}
                                disabled={isRemovingParticipant && participantToRemove === participant.id}
                                className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-xs disabled:opacity-50"
                                title="Remove participant"
                              >
                                {isRemovingParticipant && participantToRemove === participant.id ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-700 mx-auto"></div>
                                ) : (
                                  "Remove"
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowManageParticipantsModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Shared Contact Modal */}
      {showAddContactModal && contactToAdd && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center mb-4">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                  contactType === 'veterinarian' ? 'bg-green-100' : 'bg-purple-100'
                }`}>
                  <span className={`text-2xl ${
                    contactType === 'veterinarian' ? 'text-green-600' : 'text-purple-600'
                  }`}>
                    {contactType === 'veterinarian' ? '🏥' : '🔨'}
                  </span>
                </div>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Add {contactType === 'veterinarian' ? 'Veterinarian' : 'Farrier'} to Your Contacts
                </h3>
                <p className="text-sm text-gray-500">
                  Review the contact information before adding to your contacts.
                </p>
              </div>
              
              {/* Contact Details */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                <div className="space-y-2">
                  <div>
                    <span className="font-medium text-sm text-gray-700">Name:</span>
                    <p className="text-sm text-gray-900">{contactToAdd.name}</p>
                  </div>
                  {contactToAdd.clinic_name && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Clinic:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.clinic_name}</p>
                    </div>
                  )}
                  {contactToAdd.phone && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Phone:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.phone}</p>
                    </div>
                  )}
                  {contactToAdd.email && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Email:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.email}</p>
                    </div>
                  )}
                  {contactToAdd.address && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Address:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.address}</p>
                    </div>
                  )}
                  {contactToAdd.specialties && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Specialties:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.specialties}</p>
                    </div>
                  )}
                  {contactToAdd.notes && (
                    <div>
                      <span className="font-medium text-sm text-gray-700">Notes:</span>
                      <p className="text-sm text-gray-900">{contactToAdd.notes}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowAddContactModal(false);
                    setContactToAdd(null);
                    setContactType('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => addSharedContact(contactToAdd, contactType)}
                  disabled={isAddingContact}
                  className={`px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    contactType === 'veterinarian' ? 'bg-green-600 hover:bg-green-700' : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {isAddingContact ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Adding...
                    </div>
                  ) : (
                    `Add ${contactType === 'veterinarian' ? 'Veterinarian' : 'Farrier'}`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Campfire Confirmation Modal */}
      {showDeleteCampfireConfirm && campfireToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <span className="text-2xl">🗑️</span>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Campfire
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete "{campfireToDelete.name}"? This action cannot be undone and will remove all users and messages.
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteCampfireConfirm(false);
                    setCampfireToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={deleteCampfire}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete Campfire
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Participant Confirmation Modal */}
      {showRemoveParticipantConfirm && participantToRemove && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <X className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Remove Member
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to remove "{participantToRemove.name}" from this campfire? They will no longer have access to the campfire.
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowRemoveParticipantConfirm(false);
                    setParticipantToRemove(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={removeParticipantFromEdit}
                  disabled={isRemovingParticipant}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isRemovingParticipant ? "Removing..." : "Remove Member"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Campfire Modal */}
      {showCreateCampfireModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  🔥 Create Campfire
                </h3>
                <button
                  onClick={() => {
                    setShowCreateCampfireModal(false);
                    clearSelectedParticipants();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campfire Name *
                  </label>
                  <input
                    type="text"
                    value={newCampfireName}
                    onChange={(e) => setNewCampfireName(e.target.value)}
                    placeholder="e.g., Friday Night Campfire"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newCampfireDescription}
                    onChange={(e) => setNewCampfireDescription(e.target.value)}
                    placeholder="What's this campfire about?"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                

                <div>
                  <div className="space-y-3">
                    <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={() => openParticipantSearch('create')}
                      className="px-3 py-2 border border-gray-300 bg-orange-300 rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-center flex items-center justify-center gap-2"
                    >
                      <span className="text-black-500">Search and invite users...</span>
                      <UserPlus size={16} className="text-gray-400" />
                    </button>
                    </div>
                    {usersToInvite.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-700">Users to invite:</p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {usersToInvite.map((user) => (
                            <div key={user.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                              <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                                  {user.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-900">{user.name}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeUserFromInviteList(user.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This will be a private campfire. Only invited users can join.
                  </p>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateCampfireModal(false);
                    clearSelectedParticipants();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={createCampfire}
                  disabled={isCreatingCampfire || !newCampfireName.trim() || !newCampfireDescription.trim()}
                  className="flex-1 px-4 py-2 bg-orange-300 text-black rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingCampfire ? "Creating..." : "Create Campfire"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participant Search Modal */}
      {showParticipantSearchModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <UserPlus size={20} />
                  {searchMode === 'create' ? 'Add Participants' : 'Edit Participants'}
                </h3>
                <button
                  onClick={() => setShowParticipantSearchModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search for users to invite
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="email"
                      value={participantSearchTerm}
                      onChange={(e) => setParticipantSearchTerm(e.target.value)}
                      placeholder="Enter email address"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      onClick={() => searchParticipants(participantSearchTerm)}
                      disabled={!participantSearchTerm.trim() || isSearchingParticipants}
                      className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSearchingParticipants ? "..." : "Search"}
                    </button>
                  </div>
                </div>

                {/* Search results */}
                {participantSearchResults.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Click on the user to add to the invite list
                    </label>
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {participantSearchResults.map((user) => (
                        <div
                          key={user.id}
                          onClick={() => sendParticipantInvite(user, editingCampfire)}
                          className="p-2 border rounded-md cursor-pointer transition-colors bg-orange-50 hover:bg-orange-100"
                        >
                          <div className="font-medium text-gray-900">{user.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="text-xs text-gray-600 bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <p><strong>Note:</strong> Search requires exact email matches. Selected users will receive an invitation to join the campfire.</p>
                </div>
              </div>
              
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowParticipantSearchModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}